import React, { useEffect, useState } from 'react';
import { Col, Form, message, Row, Select, Table, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { api, endpoints } from '../../api';
import { ExpertArchivalSvg, ExpertCandidateSvg, ExpertDisabledSvg, ExpertReadySvg } from '../../icons';
import { routerPath } from '../../router/routerPath';
import {
  FormBlock,
  BlueLayout,
  CustomButton,
  CustomTableContainer,
  FlexEndCol,
  FormInput,
  FormItem,
  CustomSelect,
  GridItem,
} from '../../styles/styled';
import { sortStatus, sortSubjectAreas } from '../../utils/sortObject';
import { StatusesType } from '../ApplicationPage/types';
import { SubjectAreasType, SubjectsType } from '../NewApplicationPage/types';
import { ExpertsResponseType, ExpertsType, ExpertTeachingExperiencesType, ExpertType } from '../NewExpertPage/types';
import { ExpertPageContainer } from './styled';
import { ExpertXlsResponseType, SearchType } from './types';

const { Option } = Select;

export type PaginationType = {
  pagination: number;
  perPage: number;
  count: number;
};

export const ExpertPage = () => {
  const [paginationState, setPaginationState] = useState<PaginationType>({
    pagination: 1,
    perPage: 10,
    count: 0,
  });

  const [expertsState, setExpertsState] = useState<ExpertsType>([]);
  const [subjectsState, setSubjectsState] = useState<SubjectsType>([]);
  const [subjectAreasState, setSubjectAreasState] = useState<SubjectAreasType>([]);
  const [statusesState, setStatusesState] = useState<StatusesType>([]);
  const [searchLoad, setLoad] = useState<'clear' | 'search' | 'neutral'>('clear');
  const [searchStatus, setSearchStatus] = useState<SearchType>({});
  const [expertsXlsLink, setExpertXlsLink] = useState<string | null>(null);

  const expertTableColumns = [
    {
      title: '№ п/п',
      dataIndex: 'expert_key',
      key: 'expert_key',
      render: (value: string, expert: ExpertType) => (
        <p>
          {expert.key !== undefined
            ? (paginationState.pagination === 1 ? 0 : paginationState.pagination) * paginationState.perPage +
              (paginationState.pagination === 1 ? expert.key + 1 : expert.key)
            : 1}
        </p>
      ),
    },
    {
      title: 'ФИО эксперта',
      dataIndex: 'first_name',
      key: 'first_name',
      render: (value: string, expert: ExpertType) => (
        <p className="text-link">
          <Link
            to={`${routerPath.expertProfile}/${expert.id}`}
          >{`${expert.last_name}  ${expert.first_name} ${expert.patronymic}  `}</Link>
        </p>
      ),
    },
    {
      title: 'Организация',
      dataIndex: 'name_of_organization',
      key: 'name_of_organization',
    },
    {
      title: 'Предметная область',
      dataIndex: 'expert_teaching_experiences',
      key: 'expert_teaching_experiences',
      render: (value: ExpertTeachingExperiencesType) => {
        return (
          <p>
            {value
              .map((el) => el?.subject_area_general)
              .map((el) => el?.title)
              .join(', ')}
          </p>
        );
      },
    },
    {
      title: 'Учебный предмет',
      dataIndex: 'expert_teaching_experiences',
      key: 'expert_teaching_experiences',
      render: (value: ExpertTeachingExperiencesType) => {
        return (
          <p>
            {value
              .map((el) => el.subjects_general)
              .map((el) => el[0]?.title)
              .join(', ')}
          </p>
        );
      },
    },
    {
      title: 'Контакты',
      dataIndex: 'contacts',
      key: 'contacts',
      render: (value: string, expert: ExpertType) => (
        <p>
          {expert.phone} <br /> {expert.email}
        </p>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (value: string, expert: ExpertType) => {
        switch (statusesState.find((el) => el.id === expert?.status?.id)?.code) {
          case 'ready':
            return (
              <Tooltip title="Готов к экспертизе" placement="left">
                <div>
                  <ExpertReadySvg />
                </div>
              </Tooltip>
            );
          case 'disabled':
            return (
              <Tooltip title="Не активен" placement="left">
                <div>
                  <ExpertDisabledSvg />
                </div>
              </Tooltip>
            );
          case 'candidate':
            return (
              <Tooltip title="Кандидат в эксперты" placement="left">
                <div>
                  <ExpertCandidateSvg />
                </div>
              </Tooltip>
            );
          case 'archival':
            return (
              <Tooltip title="Архивный" placement="left">
                <div>
                  <ExpertArchivalSvg />
                </div>
              </Tooltip>
            );
          default:
            return <p>{expert?.status?.id}</p>;
        }
      },
    },
  ];

  useEffect(() => {
    (async () => {
      const subjectsResponse = await api.get<SubjectsType>(endpoints.getSubjects());
      const subjectsAreaResponse = await api.get<SubjectAreasType>(endpoints.getSubjectAreas());
      const statusesResponse = await api.get<StatusesType>(endpoints.getExpertStatuses());
      const expertsXlsResponse = await api.get<ExpertXlsResponseType>(endpoints.getExpertsXls());

      setSubjectAreasState(subjectsAreaResponse.data.sort(sortSubjectAreas));
      setStatusesState(statusesResponse.data.sort(sortStatus));
      setSubjectsState(subjectsResponse.data.sort(sortSubjectAreas));
      setExpertXlsLink(expertsXlsResponse?.data?.xls_file_path);
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (searchLoad === 'clear') {
          const response = await api.get<ExpertsResponseType>(
            endpoints.getExperts({ perPage: paginationState.perPage, page: paginationState.pagination - 1 }),
          );

          setPaginationState((prevState) => {
            return { ...prevState, count: response.data.count };
          });

          setExpertsState(
            response.data.experts.map((el, index) => {
              return {
                ...el,
                key: index,
              };
            }),
          );
        } else if (searchLoad === 'search') {
          const params = {
            perPage: paginationState.perPage,
            subject: searchStatus.subject,
            page: paginationState.pagination - 1,
            subject_area: searchStatus.subject_area,
            status: searchStatus.status,
            fio: searchStatus.fio,
          };

          const response = await api.get<ExpertsResponseType>(endpoints.getExperts(params));

          setLoad('neutral');

          setPaginationState((prevState) => {
            return { ...prevState, count: response.data.count };
          });

          setExpertsState(
            response.data.experts.map((el, index) => {
              return {
                ...el,
                key: index,
              };
            }),
          );
        }
      } catch (err) {}
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchLoad, paginationState.pagination, paginationState.perPage]);

  const onSearchHandle = () => {
    setLoad('search');
    setPaginationState((prevState) => {
      return { ...prevState, paginationState: 1 };
    });
  };

  const onPaginationChangeHandle = (value, pageSize) => {
    setPaginationState({ ...paginationState, pagination: value, perPage: pageSize });
    setLoad((prevState) => (prevState === 'neutral' ? 'search' : prevState));
  };

  return (
    <ExpertPageContainer>
      <BlueLayout>Эксперты</BlueLayout>
      <FormBlock>
        <Form>
          <Row gutter={[15, 15]}>
            <Col span={12}>
              <FormItem>
                <p>Учебный предмет</p>
                <CustomSelect
                  onChange={(value, option) => {
                    setSearchStatus({
                      ...searchStatus,
                      subject: parseInt(Object.values(option)[0]),
                    });
                  }}
                  loading={subjectsState?.length === 0}
                  value={subjectsState?.find((el) => el.id === searchStatus?.subject)?.title}
                  showSearch
                >
                  {subjectsState
                    .filter((el) =>
                      subjectAreasState.find((e) =>
                        searchStatus.subject_area
                          ? el.subject_area_general.id === searchStatus.subject_area
                          : e.id === el.subject_area_general.id,
                      ),
                    )
                    .map((el) => {
                      return (
                        <Option key={el.id} value={el.title}>
                          {el.title}
                        </Option>
                      );
                    })}
                </CustomSelect>
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem>
                <p>Предметная область</p>
                <CustomSelect
                  onChange={(value, option) => {
                    setSearchStatus({
                      ...searchStatus,
                      subject_area: parseInt(Object.values(option)[0]),
                      subject: undefined,
                    });
                  }}
                  loading={subjectAreasState?.length === 0}
                  value={subjectAreasState?.find((el) => el.id === searchStatus?.subject_area)?.title}
                  showSearch
                >
                  {subjectAreasState.map((el) => {
                    return (
                      <Option key={el.id} value={el.title}>
                        {el.title}
                      </Option>
                    );
                  })}
                </CustomSelect>
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem>
                <p>ФИО</p>
                <FormInput
                  value={searchStatus?.fio}
                  onChange={(e) => setSearchStatus({ ...searchStatus, fio: e.target.value })}
                />
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem>
                <p>Статус</p>
                <CustomSelect
                  onChange={(value, option) => {
                    setSearchStatus({
                      ...searchStatus,
                      status: parseInt(Object.values(option)[0]),
                    });
                  }}
                  loading={statusesState?.length === 0}
                  value={statusesState?.find((el) => el.id === searchStatus?.status)?.name}
                  showSearch
                >
                  {statusesState.map((el) => {
                    return (
                      <Option key={el.id} value={el.name}>
                        {el.name}
                      </Option>
                    );
                  })}
                </CustomSelect>
              </FormItem>
            </Col>
            <FlexEndCol span={12}>
              <CustomButton onClick={onSearchHandle}>Найти</CustomButton>
            </FlexEndCol>
            <FlexEndCol span={12}>
              <CustomButton
                onClick={() => {
                  setLoad('clear');
                  setSearchStatus({});
                }}
                lined
              >
                Очистить
              </CustomButton>
            </FlexEndCol>
          </Row>
        </Form>
      </FormBlock>

      <CustomTableContainer>
        <GridItem align="flex-end" justify="flex-end" direction="row" gap="20px">
          {expertsXlsLink ? (
            <a href={expertsXlsLink} download target="_blank" rel="noreferrer">
              <CustomButton style={{ float: 'right' }} width="300px">
                скачать реестр экспертов
              </CustomButton>
            </a>
          ) : (
            <CustomButton
              style={{ float: 'right' }}
              onClick={() => message.warn('Подождите, идет загрузка')}
              width="300px"
            >
              скачать реестр экспертов
            </CustomButton>
          )}
          <Link to={routerPath.newExpert}>
            <CustomButton style={{ float: 'right' }} width="300px" lined>
              добавить эксперта
            </CustomButton>
          </Link>
        </GridItem>
        <Table
          pagination={{
            pageSize: paginationState.perPage,
            total: paginationState.count,
            current: paginationState.pagination,
            onChange: onPaginationChangeHandle,
          }}
          columns={expertTableColumns}
          dataSource={expertsState}
          className={'table'}
        ></Table>
      </CustomTableContainer>
    </ExpertPageContainer>
  );
};
